/* GOLDENAGE product page: full screen photo viewer.
 *
 * David, 2026-10-02: "i cannot click into a full screen zoom of the product ... pretty simple
 * feature". Tapping a gallery photo opens it on black, full bleed, and from there:
 *
 *   phone    swipe left/right between photos, pinch to zoom, double tap to zoom, drag to pan
 *   desktop  wheel or a click to zoom, drag to pan, the arrows or the keyboard between photos
 *   both     the X or Esc closes, and the gallery underneath lands on the photo you left on
 *
 * Nothing was ported wholesale. The two lightboxes the fleet already owns did not fit:
 * bruised-rebuild/assets/photoswipe.js is PhotoSwipe 4 behind that theme's own runtime
 * (window.theme.a11y, window.theme.assets, a sprite skin), and parkfriends-rebuild's GLightbox is
 * 70 KB of vendor with no double tap. This is the mechanism the size chart viewer in
 * sections/product-main.liquid already proved on this brand (tap to zoom, pan inside the frame),
 * grown into a multi photo viewer, so it is 1 file, it is on brand, and it is only on the PDP.
 *
 * A DRAG IS NOT A CLICK. assets/ga-rail.js already eats the click that ends a mouse drag, in the
 * capture phase on the gallery track, so a drag never reaches these photos at all. The slop guard
 * below is for a finger, where a scroll can still end in a click on some browsers.
 */
(function () {
  if (window.gaLightbox) return;

  var MAX_SCALE = 4;      /* as far as a pinch or the wheel may go */
  var TAP_SCALE = 2.5;    /* where a double tap or a desktop click lands */
  var SWIPE_PX = 45;      /* track travel that commits to the next photo */
  var SWIPE_V = 0.35;     /* px/ms of flick that commits regardless of travel */
  var TAP_SLOP = 8;       /* movement that turns a tap into a drag */
  var DOUBLE_MS = 320;    /* two taps inside this count as a double tap */

  var root, stage, track, countEl, prevBtn, nextBtn;
  var gallery = null;
  var photos = [];        /* the gallery's own <img>, in gallery order */
  var slides = [];        /* { wrap, img } one per photo, built once */
  var index = 0;
  var scale = 1, tx = 0, ty = 0;   /* tx/ty are screen px, applied outside the scale */
  var live = {};          /* pointers down on the stage */
  var one = null;         /* the single pointer drag in flight */
  var two = null;         /* the pinch in flight */
  var lastTap = 0, lastTapX = 0, lastTapY = 0;
  var trackOffset = 0;    /* how far the swipe has dragged the row, px */

  /* ---------------------------------------------------------------- helpers */

  /* the biggest candidate the gallery already asked Shopify for (2000w), so the viewer is sharp
     without a second set of URLs to keep in step with the Liquid */
  function biggest(img) {
    var set = img.getAttribute('srcset') || '';
    var best = '', bestW = 0;
    set.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = parseInt(bits[1] || '0', 10);
      if (bits[0] && w >= bestW) { bestW = w; best = bits[0]; }
    });
    return best || img.currentSrc || img.getAttribute('src') || '';
  }

  function stageWidth() { return stage ? stage.clientWidth : window.innerWidth; }

  /* how far the zoomed photo may travel before its edge shows */
  function limits() {
    var im = slides[index] && slides[index].img;
    if (!im) return { x: 0, y: 0 };
    return {
      x: Math.max(0, (im.offsetWidth * scale - stage.clientWidth) / 2),
      y: Math.max(0, (im.offsetHeight * scale - stage.clientHeight) / 2)
    };
  }

  function clampPan() {
    var l = limits();
    if (tx > l.x) tx = l.x;
    if (tx < -l.x) tx = -l.x;
    if (ty > l.y) ty = l.y;
    if (ty < -l.y) ty = -l.y;
  }

  function paintSlide() {
    var s = slides[index];
    if (!s) return;
    s.img.style.transform = 'translate3d(' + tx.toFixed(2) + 'px, ' + ty.toFixed(2) + 'px, 0) scale(' + scale.toFixed(4) + ')';
    root.classList.toggle('is-zoomed', scale > 1.01);
  }

  function paintTrack(animate) {
    track.classList.toggle('is-anim', !!animate);
    track.style.transform = 'translate3d(' + (-index * stageWidth() + trackOffset).toFixed(2) + 'px, 0, 0)';
  }

  function resetZoom() {
    scale = 1; tx = 0; ty = 0;
    paintSlide();
  }

  /* the current photo and its two neighbours carry a src; the rest stay empty until you reach them,
     so opening the viewer does not pull seven 2000px photos down a phone connection.
     Each slide shows the gallery's own photo first, which the browser already has in cache, so the
     viewer is never a black screen with nothing in it while the 2000px file comes down. */
  function seed(slot) {
    if (!slot || slot.seeded) return;
    slot.seeded = true;
    slot.img.setAttribute('src', slot.small);
    if (slot.src === slot.small) return;
    var big = new Image();
    big.onload = function () { slot.img.setAttribute('src', slot.src); };
    big.src = slot.src;
  }

  function loadAround() {
    /* one slot per call, so each onload closes over its own slide. A var in the loop body is shared
       by every closure the loop makes, which put all four upgrades on the last slide. */
    for (var i = index - 1; i <= index + 1; i++) seed(slides[i]);
  }

  function paintCount() {
    if (countEl) countEl.textContent = (index + 1) + ' / ' + slides.length;
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index >= slides.length - 1;
    slides.forEach(function (s, i) { s.wrap.setAttribute('aria-hidden', i === index ? 'false' : 'true'); });
  }

  function goTo(i, animate) {
    var next = Math.max(0, Math.min(slides.length - 1, i));
    if (next !== index) {
      var old = slides[index];
      if (old) old.img.style.transform = '';
      index = next;
      resetZoom();
    }
    trackOffset = 0;
    paintTrack(animate !== false);
    paintCount();
    loadAround();
  }

  /* ---------------------------------------------------------------- building */

  function build() {
    root = document.querySelector('[data-ga-lb]');
    if (!root) return false;
    stage = root.querySelector('[data-ga-lb-stage]');
    track = root.querySelector('[data-ga-lb-track]');
    countEl = root.querySelector('[data-ga-lb-count]');
    prevBtn = root.querySelector('[data-ga-lb-prev]');
    nextBtn = root.querySelector('[data-ga-lb-next]');
    gallery = document.getElementById('gallery');
    if (!stage || !track || !gallery) return false;

    photos = Array.prototype.slice.call(gallery.querySelectorAll('img.product__item-img'));
    if (!photos.length) return false;

    photos.forEach(function (img, i) {
      var wrap = document.createElement('div');
      wrap.className = 'ga-lb__slide';
      var big = document.createElement('img');
      big.className = 'ga-lb__img';
      big.alt = img.getAttribute('alt') || '';
      big.draggable = false;
      wrap.appendChild(big);
      track.appendChild(wrap);
      slides.push({ wrap: wrap, img: big, src: biggest(img), small: img.currentSrc || img.getAttribute('src') || '', seeded: false });

      /* the gallery photo is the trigger, and it says so to a keyboard and a screen reader */
      img.classList.add('ga-gallery__zoomable');
      img.setAttribute('role', 'button');
      img.setAttribute('tabindex', '0');
      img.setAttribute('aria-label', 'Open photo ' + (i + 1) + ' full screen');
    });
    return true;
  }

  /* ---------------------------------------------------------------- open / close */

  /* which photo the shopper is actually looking at: the one nearest the middle of the row */
  function galleryIndex() {
    var gr = gallery.getBoundingClientRect();
    var mid = gr.left + gr.width / 2;
    var best = 0, bd = Infinity;
    photos.forEach(function (im, i) {
      var r = im.getBoundingClientRect();
      var d = Math.abs(r.left + r.width / 2 - mid);
      if (d < bd) { bd = d; best = i; }
    });
    return best;
  }

  function open(i) {
    if (!slides.length) return;
    index = Math.max(0, Math.min(slides.length - 1, i || 0));
    resetZoom();
    trackOffset = 0;
    root.hidden = false;
    document.documentElement.classList.add('ga-modal-open');
    paintTrack(false);
    paintCount();
    loadAround();
    var close = root.querySelector('[data-ga-lb-close]');
    if (close) close.focus({ preventScroll: true });
  }

  function close() {
    if (!root || root.hidden) return;
    root.hidden = true;
    var modal = document.querySelector('[data-ga-modal]');
    var zoom = document.querySelector('[data-ga-zoom]');
    if ((!modal || modal.hidden) && (!zoom || zoom.hidden)) document.documentElement.classList.remove('ga-modal-open');
    /* the gallery lands on the photo you left on */
    var back = photos[index];
    if (back) {
      gallery.scrollTo({ left: back.offsetLeft - (gallery.clientWidth - back.offsetWidth) / 2, behavior: 'auto' });
      back.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------- zoom maths */

  /* pointer position measured from the middle of the stage, which is where a slide sits at rest */
  function fromCentre(x, y) {
    var r = stage.getBoundingClientRect();
    return { x: x - (r.left + r.width / 2), y: y - (r.top + r.height / 2) };
  }

  /* zoom to `next` and keep the point under the finger where it is */
  function zoomTo(next, atX, atY) {
    next = Math.max(1, Math.min(MAX_SCALE, next));
    var p = fromCentre(atX, atY);
    var cx = (p.x - tx) / scale;
    var cy = (p.y - ty) / scale;
    scale = next;
    if (scale <= 1.001) { scale = 1; tx = 0; ty = 0; }
    else { tx = p.x - cx * scale; ty = p.y - cy * scale; }
    clampPan();
    paintSlide();
  }

  function toggleZoom(atX, atY) {
    if (scale > 1.01) zoomTo(1, atX, atY);
    else zoomTo(TAP_SCALE, atX, atY);
  }

  /* ---------------------------------------------------------------- gestures */

  function pointerCount() { return Object.keys(live).length; }

  /* is this point on the photo, or on the black around it? e.target cannot answer: the moment a
     pointer is captured, Chromium retargets pointerup (and the mouse events behind it) to the
     capturing element, so every tap looked like a tap on the stage and closed the viewer. */
  function onPhoto(x, y) {
    var im = slides[index] && slides[index].img;
    if (!im) return false;
    var r = im.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  function onDown(e) {
    if (e.target.closest('button')) return;
    live[e.pointerId] = { x: e.clientX, y: e.clientY };

    if (pointerCount() === 2) {
      var ids = Object.keys(live);
      var a = live[ids[0]], b = live[ids[1]];
      two = {
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: scale,
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2
      };
      one = null;
      track.classList.remove('is-anim');
      return;
    }
    if (pointerCount() === 1) {
      one = {
        id: e.pointerId,
        x: e.clientX, y: e.clientY,
        tx: tx, ty: ty,
        t: e.timeStamp, lastX: e.clientX, lastT: e.timeStamp, v: 0,
        moved: false
      };
      track.classList.remove('is-anim');
    }
  }

  function onMove(e) {
    if (!live[e.pointerId]) return;
    live[e.pointerId] = { x: e.clientX, y: e.clientY };

    if (two && pointerCount() >= 2) {
      var ids = Object.keys(live);
      var a = live[ids[0]], b = live[ids[1]];
      var d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomTo(two.scale * (d / two.dist), two.midX, two.midY);
      e.preventDefault();
      return;
    }
    if (!one || one.id !== e.pointerId) return;

    var dx = e.clientX - one.x, dy = e.clientY - one.y;
    if (!one.moved && Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return;
    if (!one.moved) {
      one.moved = true;
      /* only now, so a plain tap keeps its real target and the viewer can tell photo from black */
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    }

    if (scale > 1.01) {
      /* zoomed: the drag pans the photo */
      tx = one.tx + dx;
      ty = one.ty + dy;
      clampPan();
      paintSlide();
    } else {
      /* at rest: the drag moves the row, and the first and last photo pull back */
      var over = (index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0);
      trackOffset = over ? dx * 0.35 : dx;
      paintTrack(false);
    }
    var dt = e.timeStamp - one.lastT;
    if (dt > 0) {
      one.v = (e.clientX - one.lastX) / dt;
      one.lastX = e.clientX;
      one.lastT = e.timeStamp;
    }
    e.preventDefault();
  }

  function onUp(e) {
    var had = live[e.pointerId];
    delete live[e.pointerId];
    try { stage.releasePointerCapture(e.pointerId); } catch (err) {}
    if (pointerCount() < 2) two = null;
    if (!had || !one || one.id !== e.pointerId) { if (!pointerCount()) one = null; return; }

    var moved = one.moved;
    var v = one.v;
    var dx = e.clientX - one.x;
    one = null;

    if (!moved) {
      /* a tap: the black around the photo closes, the photo itself zooms */
      if (!onPhoto(e.clientX, e.clientY)) { close(); return; }
      var now = e.timeStamp;
      var near = Math.abs(e.clientX - lastTapX) < 40 && Math.abs(e.clientY - lastTapY) < 40;
      if (now - lastTap < DOUBLE_MS && near) {
        lastTap = 0;
        toggleZoom(e.clientX, e.clientY);
        return;
      }
      lastTap = now; lastTapX = e.clientX; lastTapY = e.clientY;
      /* a mouse has no second tap coming: one click zooms, as David asked for on desktop */
      if (e.pointerType === 'mouse') toggleZoom(e.clientX, e.clientY);
      return;
    }

    if (scale > 1.01) return;   /* a pan settles where it is */

    var commit = Math.abs(dx) > SWIPE_PX || Math.abs(v) > SWIPE_V;
    if (commit && dx < 0) goTo(index + 1, true);
    else if (commit && dx > 0) goTo(index - 1, true);
    else { trackOffset = 0; paintTrack(true); }
  }

  function onWheel(e) {
    e.preventDefault();
    /* follows how far the wheel or trackpad actually moved, clamped so one hard flick cannot jump
       from fit to the far end of the zoom in a single event */
    var d = Math.max(-500, Math.min(500, e.deltaY));
    zoomTo(scale * Math.exp(-d * 0.0022), e.clientX, e.clientY);
  }

  /* ---------------------------------------------------------------- wiring */

  function wire() {
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('dblclick', function (e) { e.preventDefault(); });
    /* the native image drag would steal the pointer mid gesture, same trap as the rails */
    stage.addEventListener('dragstart', function (e) { e.preventDefault(); });

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1, true); });
    var closeBtn = root.querySelector('[data-ga-lb-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    var expand = document.querySelector('[data-ga-lb-open]');
    if (expand) expand.addEventListener('click', function (e) { e.preventDefault(); open(galleryIndex()); });

    /* the trigger. A mouse drag never gets here: ga-rail.js swallows that click on the track in
       the capture phase. The slop below is the finger case. */
    var downAt = null;
    gallery.addEventListener('pointerdown', function (e) { downAt = { x: e.clientX, y: e.clientY }; }, true);
    gallery.addEventListener('click', function (e) {
      var img = e.target.closest('img.product__item-img');
      if (!img) return;
      if (downAt && (Math.abs(e.clientX - downAt.x) > TAP_SLOP || Math.abs(e.clientY - downAt.y) > TAP_SLOP)) return;
      e.preventDefault();
      open(photos.indexOf(img));
    });
    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var img = e.target.closest && e.target.closest('img.product__item-img');
      if (!img) return;
      e.preventDefault();
      open(photos.indexOf(img));
    });

    document.addEventListener('keydown', function (e) {
      if (!root || root.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, true); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, true); return; }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(scale * 1.4, window.innerWidth / 2, window.innerHeight / 2); return; }
      if (e.key === '-') { e.preventDefault(); zoomTo(scale / 1.4, window.innerWidth / 2, window.innerHeight / 2); }
    });

    /* a turned phone changes the slide width, so the row has to be re-seated */
    window.addEventListener('resize', function () {
      if (!root || root.hidden) return;
      resetZoom();
      trackOffset = 0;
      paintTrack(false);
    });
  }

  function start() {
    if (!build()) return;
    wire();
    window.gaLightbox = { open: open, close: close, goTo: goTo, count: function () { return slides.length; }, at: function () { return index; }, scale: function () { return scale; } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
